import React from 'react';
import {
  Box,
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import CorporateScopeSelector from '../../components/CorporateScopeSelector';
import Layout from '../../components/Layout';
import { useCompanyStore } from '../../services/company';
import { createUserAlyveSso, fetchAlyvePolicies, fetchAlyveUsers } from '../../services/alyve';
import { showError, showSuccess } from '../../services/toaster';
import { UserData } from '../../types/user';
import { AlyvePolicy, AlyvePolicyStatus, AlyvePolicyType } from '../../types/alyve';

const ROWS_PER_PAGE = 10;

function statusColor(status: AlyvePolicyStatus) {
  if (status === 'active') return 'success';
  if (status === 'cancelled') return 'warning';
  if (status === 'failed') return 'error';
  return 'info';
}

const Alyve = () => {
  const navigate = useNavigate();
  const { globalCompanyId } = useCompanyStore();
  const selectedCorporateId = globalCompanyId && globalCompanyId !== 'general' ? globalCompanyId : '';
  const [rows, setRows] = React.useState<AlyvePolicy[]>([]);
  const [users, setUsers] = React.useState<UserData[]>([]);
  const [status, setStatus] = React.useState<AlyvePolicyStatus | 'all'>('all');
  const [search, setSearch] = React.useState('');
  const [currentPage, setCurrentPage] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [usersLoading, setUsersLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createUserIds, setCreateUserIds] = React.useState<string[]>([]);
  const [userSearchInput, setUserSearchInput] = React.useState('');
  const [userSearch, setUserSearch] = React.useState('');
  const [existingPolicyUserIds, setExistingPolicyUserIds] = React.useState<Set<string> | null>(null);
  const [eligibilityError, setEligibilityError] = React.useState('');
  const [createErrors, setCreateErrors] = React.useState<{ userId: string; message: string }[]>([]);
  const [createProgress, setCreateProgress] = React.useState(0);
  const [createTotal, setCreateTotal] = React.useState(0);
  const mountedRef = React.useRef(false);
  const submittingRef = React.useRef(false);
  const [createPolicyType, setCreatePolicyType] = React.useState<AlyvePolicyType>('OPD');

  React.useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadPolicies = React.useCallback(async () => {
    setLoading(true);
    try {
      setRows(await fetchAlyvePolicies(status, selectedCorporateId));
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to fetch Alyve policies');
    } finally {
      setLoading(false);
    }
  }, [selectedCorporateId, status]);

  React.useEffect(() => {
    let current = true;
    setUsers([]);
    setCreateUserIds([]);
    setCreateErrors([]);
    setUserSearchInput('');
    setUserSearch('');
    setUsersLoading(true);
    fetchAlyveUsers(selectedCorporateId).then(result => {
      if (current) setUsers(result.filter(user => user._id));
    }).catch(error => {
      if (current) showError(error instanceof Error ? error.message : 'Failed to fetch users');
    }).finally(() => { if (current) setUsersLoading(false); });
    return () => { current = false; };
  }, [selectedCorporateId]);

  React.useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  React.useEffect(() => {
    if (!createOpen) return;
    let current = true;
    setExistingPolicyUserIds(null);
    setEligibilityError('');
    // Match the backend's user-wide policy lookup, including corporate transfers.
    fetchAlyvePolicies('all').then(policies => {
      if (!current) return;
      const existingIds = new Set(policies.filter(policy => ['pending', 'active'].includes(policy.policyStatus)).map(policy => policy.userId));
      setExistingPolicyUserIds(existingIds);
      setCreateUserIds(ids => ids.filter(id => !existingIds.has(id)));
    }).catch(error => {
      if (current) setEligibilityError(error instanceof Error ? error.message : 'Failed to check existing Alyve policies.');
    });
    return () => { current = false; };
  }, [createOpen, selectedCorporateId]);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [search, selectedCorporateId, status]);

  const userById = React.useMemo(() => {
    return users.reduce((map, user) => {
      if (user._id) map.set(user._id, user);
      return map;
    }, new Map<string, UserData>());
  }, [users]);

  const eligibleUserIds = React.useMemo(() => existingPolicyUserIds
    ? users.filter(user => !existingPolicyUserIds.has(user._id!)).map(user => user._id!) : [], [users, existingPolicyUserIds]);

  const matchingUsers = React.useMemo(() => {
    const term = userSearch.trim().toLowerCase();
    return users.filter(user => !term || [user.name, user.email, user.phone].some(value => value?.toLowerCase().includes(term)));
  }, [users, userSearch]);
  const matchingEligibleIds = React.useMemo(() => existingPolicyUserIds
    ? matchingUsers.filter(user => !existingPolicyUserIds.has(user._id!)).map(user => user._id!) : [], [matchingUsers, existingPolicyUserIds]);

  const getUserDisplay = React.useCallback((userId: string) => {
    const user = userById.get(userId);
    return {
      name: user?.name || user?.email || user?.phone || userId,
      meta: user ? [user.email, user.phone].filter(Boolean).join(' - ') : userId,
    };
  }, [userById]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [
        row.policyNumber,
        row.coiNumber,
        row.userId,
        getUserDisplay(row.userId).name,
        getUserDisplay(row.userId).meta,
        row.corporateId,
        row.productCode,
        row.policyCode,
        row.planOrderId,
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term))
    );
  }, [getUserDisplay, rows, search]);

  const pagedRows = React.useMemo(() => {
    const start = currentPage * ROWS_PER_PAGE;
    return filteredRows.slice(start, start + ROWS_PER_PAGE);
  }, [filteredRows, currentPage]);

  const handleCreatePolicy = async () => {
    if (submittingRef.current || usersLoading || !existingPolicyUserIds) return;
    const selectedIds = Array.from(new Set(createUserIds)).filter(id => userById.has(id) && !existingPolicyUserIds.has(id));
    if (!selectedIds.length) {
      showError('Select at least one user');
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setCreateErrors([]);
    setCreateProgress(0);
    setCreateTotal(selectedIds.length);
    const failures: { userId: string; message: string }[] = [];
    let created = 0, existing = 0;
    try {
      for (const userId of selectedIds) {
        if (!mountedRef.current) return;
        try {
          const result = await createUserAlyveSso(userId, createPolicyType);
          if (!mountedRef.current) return;
          setExistingPolicyUserIds(ids => new Set(ids || []).add(userId));
          if (selectedIds.length === 1) {
            showSuccess(result.created ? 'Alyve policy created and SSO opened' : 'Existing Alyve policy SSO opened');
            window.open(result.redirectUrl, '_blank', 'noopener,noreferrer');
            setCreateOpen(false);
            setCreateUserIds([]);
            navigate(`/alyve/${result.policy._id}`);
            return;
          }
          if (result.created) created++; else existing++;
        } catch (error) {
          if (!mountedRef.current) return;
          failures.push({ userId, message: error instanceof Error ? error.message : 'Failed to create Alyve SSO' });
        }
        setCreateProgress(value => value + 1);
      }
      setCreateUserIds(failures.map(failure => failure.userId));
      setCreateErrors(failures);
      if (created || existing) showSuccess(`${created} Alyve policies created; ${existing} existing policies reused.`);
      if (failures.length) showError(`${failures.length} user request(s) failed. Review the errors and retry the selected users.`);
      else setCreateOpen(false);
      await loadPolicies();
    } finally {
      submittingRef.current = false;
      if (mountedRef.current) setSubmitting(false);
    }
  };

  const closeCreate = () => {
    if (submittingRef.current) return;
    setCreateOpen(false);
    setCreateUserIds([]);
    setCreateErrors([]);
  };

  return (
    <Layout appBarTitle="Alyve Policies">
      <Box sx={{ display: 'grid', gap: 2 }}>
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <CorporateScopeSelector />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
                <TextField size="small" label="Search policies" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 280 }} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value as AlyvePolicyStatus | 'all')}>
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
                    setExistingPolicyUserIds(null); setUserSearchInput(''); setUserSearch(''); setCreateOpen(true);
                  }} disabled={submitting}>
                    Create Policy
                  </Button>
                  <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadPolicies} disabled={loading}>
                    Refresh
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h6">Policies</Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedCorporateId ? 'Showing policies for the selected corporate scope.' : 'Showing general policy scope.'}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                Open a row to manage SSO, cancellation, and members.
              </Typography>
            </Stack>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Policy</TableCell>
                    <TableCell>COI</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Members</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.map((row) => {
                    const userDisplay = getUserDisplay(row.userId);
                    return (
                      <TableRow key={row._id} hover>
                        <TableCell>{row.policyNumber}</TableCell>
                        <TableCell>{row.coiNumber}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{userDisplay.name}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                            {userDisplay.meta}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.policyType || '-'}</TableCell>
                        <TableCell><Chip size="small" label={row.policyStatus} color={statusColor(row.policyStatus) as any} /></TableCell>
                        <TableCell>{row.members?.length || 0}</TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => navigate(`/alyve/${row._id}`)}>
                            Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!pagedRows.length && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">{loading ? 'Loading...' : 'No Alyve policies found'}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[ROWS_PER_PAGE]}
              component="div"
              count={filteredRows.length}
              rowsPerPage={ROWS_PER_PAGE}
              page={currentPage}
              onPageChange={(_, page) => setCurrentPage(page)}
            />
          </CardContent>
        </Card>
      </Box>

      <Dialog open={createOpen} onClose={closeCreate} maxWidth="sm" fullWidth>
        <DialogTitle>Create Alyve Policies</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">Select users without a pending or active Alyve policy. Open an existing policy from the list to manage it.</Typography>
            {!existingPolicyUserIds && !eligibilityError && <Typography role="status">Checking existing policies...</Typography>}
            {eligibilityError && <Alert severity="error">Unable to check existing policies: {eligibilityError}. Close and reopen this dialog to retry.</Alert>}
            <FormControl fullWidth disabled={usersLoading || submitting || !existingPolicyUserIds}>
              <InputLabel id="alyve-create-users-label">Users</InputLabel>
              <Select multiple labelId="alyve-create-users-label" label="Users" value={createUserIds}
                MenuProps={{ autoFocus: false, disableAutoFocusItem: true }}
                renderValue={selected => selected.map(id => getUserDisplay(id).name).join(', ')}
                onChange={(event) => {
                  const ids = typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value;
                  setCreateUserIds(existingPolicyUserIds ? ids.filter(id => !existingPolicyUserIds.has(id)) : []);
                }}>
                <ListSubheader sx={{ py: 1, lineHeight: 'normal' }}>
                  <Box onClick={event => event.stopPropagation()} onKeyDown={event => { if (event.key !== 'Escape') event.stopPropagation(); }}>
                    <Stack direction="row" spacing={1}>
                      <TextField autoFocus fullWidth size="small" type="search" label="Search users" placeholder="Name, email or phone"
                        value={userSearchInput} onChange={event => {
                          setUserSearchInput(event.target.value);
                          if (!event.target.value) setUserSearch('');
                        }} onKeyDown={event => {
                          if (event.key === 'Enter') { event.preventDefault(); setUserSearch(userSearchInput.trim()); }
                        }} />
                      <Button size="small" onClick={() => setUserSearch(userSearchInput.trim())}>Search</Button>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                      <Button size="small" disabled={submitting || !matchingEligibleIds.length}
                        onClick={() => setCreateUserIds(ids => Array.from(new Set([...ids, ...matchingEligibleIds])))}>Select all matching</Button>
                      <Typography variant="caption">{matchingUsers.length} matching · {matchingEligibleIds.length} eligible</Typography>
                    </Stack>
                  </Box>
                </ListSubheader>
                {!matchingUsers.length && <ListSubheader>No users match this search.</ListSubheader>}
                {matchingUsers.map((user) => (
                  <MenuItem key={user._id} value={user._id} disabled={!existingPolicyUserIds || existingPolicyUserIds.has(user._id!)}>
                    <Checkbox checked={createUserIds.includes(user._id!)} disabled={!existingPolicyUserIds || existingPolicyUserIds.has(user._id!)} tabIndex={-1} inputProps={{ 'aria-label': `Select ${getUserDisplay(user._id!).name}` }} />
                    {[user.name, user.email || user.phone].filter(Boolean).join(' - ') || user._id}
                    {existingPolicyUserIds?.has(user._id!) && ' — Already has a policy'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" disabled={usersLoading || submitting || !eligibleUserIds.length} onClick={() => setCreateUserIds(eligibleUserIds)}>Select all users</Button>
              <Button size="small" disabled={submitting || !createUserIds.length} onClick={() => setCreateUserIds([])}>Clear selection</Button>
              <Typography variant="body2">{createUserIds.length} selected</Typography>
            </Stack>
            {!usersLoading && !users.length && <Alert severity="info">No users found in this scope.</Alert>}
            {!usersLoading && users.length > 0 && existingPolicyUserIds && !eligibleUserIds.length && <Alert severity="info">All users in this scope already have a pending or active Alyve policy.</Alert>}
            <FormControl fullWidth disabled={submitting}>
              <InputLabel id="alyve-create-policy-type-label">Policy type</InputLabel>
              <Select labelId="alyve-create-policy-type-label" label="Policy type" value={createPolicyType} onChange={(event) => setCreatePolicyType(event.target.value as AlyvePolicyType)}>
                <MenuItem value="OPD">OPD</MenuItem>
                <MenuItem value="IPD">IPD</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Corporate ID"
              value={selectedCorporateId || 'General (Non-Corporate)'}
              disabled
            />
            {submitting && <Typography role="status">Processed {createProgress} of {createTotal} users. Keep this window open until processing finishes.</Typography>}
            {createErrors.map(error => <Alert key={error.userId} severity="error">{getUserDisplay(error.userId).name}: {error.message}</Alert>)}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreate} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePolicy} disabled={submitting || usersLoading || !existingPolicyUserIds || !createUserIds.length}>
            {submitting ? 'Processing...' : `Create for ${createUserIds.length} user(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Alyve;
